import { IPortfolioRegion } from '@api'
import { ITreeNode } from '@blueprintjs/core'
import { useState, useCallback } from 'react'
import { isEmpty } from 'lodash'
import debounce from 'lodash/fp/debounce'
import useLocationTreeContents from './useLocationTreeContents'
import useSelectedLocationTreeContents from './useSelectedLocationTreeContents'
import {
  mapLocationTreeContents,
  filtrerRegionsByString,
  LocationTreeNodeT,
  LocationNodeType,
  renderLocationTreeContents,
  renderSelectedLocationTreeContents,
  forEachNode,
  selectEachParentByFloor,
} from './utils'

type UseTreeContentsArgs = {
  regions: IPortfolioRegion[]
  disabledFloorIds: string[]
  selectedFloorIds: string[]
}

export type UseTreeContentsT = {
  searchQuery: string
  selectedFloorIds: string[]
  handleSearch: (query: string) => void
  handleClearSearch: () => void
  resetTreeNodes: () => void
  handleLocationNodeCollapse: (treeNode: LocationTreeNodeT, nodePath: number[]) => void
  handleSelectedLocationNodeClick: (treeNode: LocationTreeNodeT, nodePath: number[]) => void
  handleLocationNodeClick: (treeNode: LocationTreeNodeT, nodePath: number[]) => void
  handleSelectedLocationNodeExpand: (treeNode: LocationTreeNodeT, nodePath: number[]) => void
  handleLocationNodeExpand: (treeNode: LocationTreeNodeT, nodePath: number[]) => void
  handleSelectedLocationNodeCollapse: (treeNode: LocationTreeNodeT, nodePath: number[]) => void
  treeLocationContents: LocationTreeNodeT[]
  treeSelectedLocationContents: LocationTreeNodeT[]
}

const useTreeContents = (args: UseTreeContentsArgs): UseTreeContentsT => {
  const regions = args.regions
  const [searchQuery, setSearchQuery] = useState('')

  const { locationTreeNodes, setLocationContents, setLocationTreeNodes, resetLocationTreeNodes } =
    useLocationTreeContents(args)

  const {
    selectedFloorIds,
    selectedLocationTreeNodes,
    setSelectedLocationContents,
    resetSelectedLocationTreeNodes,
    setSelectedLocationTreeNodes,
  } = useSelectedLocationTreeContents(args)

  const debouncedSetLocationTreeNodes = makeDebounce((treeNodes: LocationTreeNodeT[]) => {
    setLocationTreeNodes(treeNodes)
  })

  const updateLocationTreeBySearch = useCallback(
    (searchQuery: string) => {
      const filteredRegions = filtrerRegionsByString(regions, searchQuery)
      const isSearchQueryEmpty = isEmpty(searchQuery)

      const treeNodes = mapLocationTreeContents(
        isSearchQueryEmpty ? regions : filteredRegions,
        !isSearchQueryEmpty
      )

      debouncedSetLocationTreeNodes(treeNodes)
    },
    [debouncedSetLocationTreeNodes, regions]
  )

  // handlers
  const handleLocationNodeClick = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    const clickedNodeType = treeNode?.nodeData?.nodeType

    setLocationContents(
      nodePath,
      (node: ITreeNode) => {
        if (clickedNodeType === LocationNodeType.Floor) {
          node.isSelected = !node.isSelected
        }

        if (clickedNodeType !== LocationNodeType.Location) {
          node.isExpanded = !treeNode.isExpanded
        }
      },
      selectEachParentByFloor
    )

    setSelectedLocationContents(
      nodePath,
      (node: ITreeNode) => {
        if (clickedNodeType === LocationNodeType.Floor) {
          node.isSelected = !node.isSelected
        }
      },
      selectEachParentByFloor,
      locationTreeNodes
    )
  }

  const handleSelectedLocationNodeClick = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    const clickedNodeType = treeNode?.nodeData?.nodeType

    setSelectedLocationContents(nodePath, (node) => {
      if (clickedNodeType !== LocationNodeType.Location) {
        node.isExpanded = !treeNode.isExpanded
      }
    })
  }

  const handleSelectNodeClick = (treeNode: LocationTreeNodeT) => {
    const nodeClickCallback = (node: ITreeNode) => {
      node.isSelected = !node.isSelected

      forEachNode((childNode) => {
        childNode.isSelected = node.isSelected
      }, node.childNodes as LocationTreeNodeT[])
    }

    setLocationContents(
      treeNode.nodeData?.nodePath || [],
      nodeClickCallback,
      selectEachParentByFloor
    )
    setSelectedLocationContents(
      treeNode.nodeData?.nodePath || [],
      nodeClickCallback,
      selectEachParentByFloor,
      locationTreeNodes
    )
  }

  const removeSelectedNode = (treeNode: LocationTreeNodeT) => {
    const nodeClickCallback = (node: ITreeNode) => {
      node.isSelected = false

      forEachNode((childNode) => {
        childNode.isSelected = false
      }, node.childNodes as LocationTreeNodeT[])
    }

    setLocationContents(
      treeNode.nodeData?.nodePath || [],
      nodeClickCallback,
      selectEachParentByFloor,
      selectedLocationTreeNodes
    )
    setSelectedLocationContents(treeNode.nodeData?.nodePath || [], nodeClickCallback)
  }

  const handleLocationNodeExpand = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    setLocationContents(nodePath, (node) => (node.isExpanded = true))
  }

  const handleLocationNodeCollapse = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    setLocationContents(nodePath, (node) => (node.isExpanded = false))
  }

  const handleSelectedLocationNodeExpand = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    setSelectedLocationContents(nodePath, (node) => (node.isExpanded = true))
  }

  const handleSelectedLocationNodeCollapse = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    setSelectedLocationContents(nodePath, (node) => (node.isExpanded = false))
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    updateLocationTreeBySearch(query)
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    updateLocationTreeBySearch('')
  }

  const resetTreeNodes = () => {
    resetSelectedLocationTreeNodes()
    resetLocationTreeNodes()
  }

  const getListOfIds = (
    selectedLocationTree: LocationTreeNodeT[]
  ): { id: string; isSelected: boolean }[] => {
    const results: { id: string; isSelected: boolean }[] = []

    const searchNodes = (nodes: LocationTreeNodeT[]) => {
      nodes.forEach((node) => {
        results.push({ id: node.id as string, isSelected: node.isSelected as boolean })
        if (node.childNodes) {
          searchNodes(node.childNodes)
        }
      })
    }

    searchNodes(selectedLocationTree)
    return results
  }

  const getModifiedId = (id: string) => {
    const newIdStringArray = id.split('-')
    if (newIdStringArray.length > 1) newIdStringArray.pop()
    return newIdStringArray.join('')
  }
  const setSelectedBuildingsAndFloors = (
    ids: { id: string; isSelected: boolean }[],
    selectedLocationTree: LocationTreeNodeT[]
  ) => {
    const setNodes = (nodes: LocationTreeNodeT[]) => {
      nodes.forEach((node) => {
        if (
          ids
            .map((a: { id: string; isSelected: boolean }) => getModifiedId(a.id))
            .includes(getModifiedId(node.id as string))
        ) {
          node.isSelected = ids.filter(
            (a: { id: string; isSelected: boolean }) =>
              getModifiedId(a.id) === getModifiedId(node.id as string)
          )[0].isSelected
        }
        if (node.childNodes) {
          setNodes(node.childNodes)
        }
      })
    }

    setNodes(selectedLocationTree)
  }

  const getUpdatedLocationTreeNodes = () => {
    setSelectedBuildingsAndFloors(
      getListOfIds(selectedLocationTreeNodes ?? locationTreeNodes),
      locationTreeNodes
    )
    return locationTreeNodes
  }
  return {
    searchQuery,
    selectedFloorIds,
    handleClearSearch,
    handleSearch,
    handleLocationNodeClick,
    handleLocationNodeExpand,
    handleLocationNodeCollapse,
    handleSelectedLocationNodeClick,
    handleSelectedLocationNodeExpand,
    handleSelectedLocationNodeCollapse,
    resetTreeNodes,
    treeLocationContents: renderLocationTreeContents(getUpdatedLocationTreeNodes(), {
      handleSelectNodeClick,
    }),
    treeSelectedLocationContents: renderSelectedLocationTreeContents(selectedLocationTreeNodes, {
      handleSelectNodeClick: removeSelectedNode,
    }),
  }
}

export default useTreeContents

const makeDebounce = debounce(200)
